import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { deleteUsers, getUsers, hasRole } from "../../services/AuthService";
import { useNotification } from "../../components/Notifications";
import { useAuth } from "../../security/AuthContext";
import { Tooltip } from "../../components/Tooltip";

export function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [isActive, setActive] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const { addNotification } = useNotification();
  const dropdownRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasRole(user, 'admin')) {
      navigate('/unauthorized');
      return;
    }
    loadUsers();
  }, [isActive]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openDropdownId !== null) {
        const dropdownRef = dropdownRefs.current.get(openDropdownId);
        if (dropdownRef && dropdownRef.contains(e.target as Node)) return;
      }
      setOpenDropdownId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  async function loadUsers() {
    try {
      const data = await getUsers();
      const filteredUsers = data.filter(user =>
        isActive ? user.deleted !== "Y" : user.deleted === "Y"
      );
      setUsers(filteredUsers);
    } catch (err: any) {
      addNotification("Error loading users " + err.message, "error");
    }
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleRefresh = () => loadUsers();

  const onDeleteUsers = async (userId: number) => {
    await deleteUsers(new Set([userId]));
    addNotification("Successfully updated user status", "success");
    loadUsers();
  };

  const filteredUsers = users.filter(user =>
    `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl relative">
      <div className="bg-white shadow-md rounded-lg overflow-visible">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-700">Users</h2>
            <div className='relative group'>
              <Link
                to="/users/create"
                className="flex text-indigo-500 h-8 w-8 items-center justify-center rounded-full hover:bg-indigo-600 hover:text-white text-lg"

              >
                <i className="fas fa-plus"></i>
              </Link>
              <Tooltip text={"Add User"}></Tooltip>
            </div>
            <div className='relative group'>
              <button
                className="fa-solid fa-rotate-right flex text-indigo-500 h-8 w-8 items-center justify-center rounded-full hover:bg-indigo-600 hover:text-white text-lg cursor-pointer"
                onClick={handleRefresh}
              ></button>
              <Tooltip text={"Refresh Users"}></Tooltip>
            </div>
            <div className="relative group">
              <button
                onClick={() => setActive(!isActive)}
                className="flex text-indigo-500 h-8 w-8 items-center justify-center rounded-full hover:bg-indigo-600 hover:text-white text-lg cursor-pointer"
              >
                <i className={`fa-solid ${isActive ? "fa-user-slash" : "fa-user"}`}></i>
              </button>
              <Tooltip text={isActive ? "Show Inactive" : "Show Active"}></Tooltip>
            </div>
          </div>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search Users..."
              className="pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-gray-800 placeholder-gray-400"
            />
            <i className="fa-solid fa-magnifying-glass text-indigo-500 absolute right-3 top-1/2 transform -translate-y-1/2 text-lg"></i>
          </div>
        </div>

        {/* User list table */}
        <div className="relative overflow-visible">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-7 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 relative z-0">
              {filteredUsers.map((user, idx) => {
                const isLast = idx === filteredUsers.length - 1;
                return (
                  <tr key={user.id} className="cursor-pointer">
                    <td colSpan={4} className="p-0">
                      <div
                        className={`grid grid-cols-[2fr_3fr_2fr_1fr] items-center px-6 py-4
                          ${isLast ? "rounded-b-lg bg-white shadow-sm" : ""} hover:bg-indigo-50`}
                        style={{
                          borderBottomLeftRadius: isLast ? "0.5rem" : undefined,
                          borderBottomRightRadius: isLast ? "0.5rem" : undefined,
                        }}
                      >
                        <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500 whitespace-nowrap">{user.email}</div>
                        <div className="text-sm text-gray-500 whitespace-nowrap">{user.roles}</div>
                        <div
                          className="relative inline-block text-left user-dropdown"
                          ref={(el) => {
                            if (el) {
                              dropdownRefs.current.set(user.id, el);
                            } else {
                              dropdownRefs.current.delete(user.id);
                            }
                          }}
                        >
                          <div className="relative group">
                            <button
                              className="flex text-indigo-500 h-8 w-8 items-center justify-center rounded-full hover:bg-indigo-600 hover:text-white text-lg cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdownId(openDropdownId === user.id ? null : user.id);
                              }}
                            >
                              <i className="fa-solid fa-ellipsis-vertical"></i>
                            </button>
                            <Tooltip text={"More Actions"}></Tooltip>
                          </div>
                          {openDropdownId === user.id && (
                            <div
                              className="absolute right-0 mt-1 w-56 bg-white border border-gray-100 rounded-t-lg rounded-b-lg shadow-sm z-50 pointer-events-auto"
                              style={{ minWidth: "14rem" }}
                            >
                              <div className='relative group'>
                                <Link
                                  to={`/users/edit/${user.id}`}
                                  onClick={() => setOpenDropdownId(null)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-900 rouned-t-lg hover:rounded-t-lg"
                                >
                                  <i className="fa-solid fa-pencil text-indigo-500"></i> Edit
                                </Link>
                                <Tooltip text={"Edit User"}></Tooltip>
                              </div>
                              <div className='relative group'>
                                <button
                                  onClick={() => {
                                    onDeleteUsers(user.id);
                                    setOpenDropdownId(null);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-red-800 hover:bg-indigo-100 hover:text-indigo-900 w-full text-left rounded-b-lg cursor-pointer"
                                >
                                  <i
                                    className={`fa-solid ${isActive ? "fa-trash text-red-800" : "fa-trash-arrow-up text-red-800"
                                      }`}
                                  ></i>{" "}
                                  {isActive ? "Delete" : "Restore"}
                                </button>
                                <Tooltip text={isActive ? "Delete User" : "Restore User"}></Tooltip>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
